import { MoneyAdapter } from '../adapters/money.adapter';
import { PriceTemplate, PriceTemplateItem } from './price-template.model';

export const DEFAULT_PRICE_TEMPLATE_PREFIX = 'COT-';

/**
 * Número que ve el usuario: prefijo configurado + últimos 6 caracteres del `_id`.
 * Se deriva del `_id` en vez de un contador atómico: no es secuencial, pero es estable
 * y no exige infraestructura nueva. Lo comparten el listado, el detalle y el reporte
 * para que el mismo documento nunca se identifique de dos formas.
 */
export function correlativoOf(priceTemplate: Pick<PriceTemplate, '_id'> | null | undefined, prefix?: string | null): string {
    return (
        (prefix || DEFAULT_PRICE_TEMPLATE_PREFIX) +
        String(priceTemplate?._id || '')
            .slice(-6)
            .toUpperCase()
    );
}

/**
 * Total en la moneda en la que se emitió la cotización.
 *
 * `totalPrice` se guarda **siempre en USD**; `currency` es la moneda del PDF —
 * `estimate-generator` multiplica por `exchangeRate` solo cuando es VES. Sin esta
 * conversión, una cotización en VES mostraba la cifra en dólares con etiqueta VES, y el
 * número no cuadraba con el documento que había recibido el cliente.
 */
export function displayTotalOf(priceTemplate: Pick<PriceTemplate, 'totalPrice' | 'currency' | 'exchangeRate'> | null | undefined): number {
    const total = priceTemplate?.totalPrice || 0;
    if (priceTemplate?.currency !== 'VES') return total;
    return total * (priceTemplate?.exchangeRate || 1);
}

/**
 * N.º de pedido con el mismo formato que Registros de pedidos (`#00123`) (FINA-3273).
 * Devuelve `''` si la cotización no está convertida o el número no llegó.
 */
export function formatOrderNumber(order: number | null | undefined): string {
    if (typeof order !== 'number' || !Number.isFinite(order)) return '';
    return '#' + String(order).padStart(5, '0');
}

/**
 * Una cotización emitida en VES muestra además su equivalente en dólares (FINA-3252):
 * `totalPrice` ya está en USD, así que es el mismo número. Devuelve null cuando la
 * cotización es en USD, para que la UI no repita el total.
 */
export function usdEquivalentOf(priceTemplate: Pick<PriceTemplate, 'totalPrice' | 'currency'> | null | undefined): number | null {
    if (priceTemplate?.currency !== 'VES') return null;
    return priceTemplate?.totalPrice || 0;
}

/**
 * Propiedades comunes de los eventos `pos_price_template_*` (FINA-3009): identifican la
 * cotización y su tamaño sin repetir la construcción en cada componente. Quien captura el
 * evento agrega lo propio (`file_type`, `bill_id`, `blocked_lines`…).
 */
export function priceTemplateEventProps(priceTemplate: Partial<PriceTemplate> | null | undefined, user?: { _id?: string; tenantId?: string } | null, prefix?: string | null): Record<string, unknown> {
    return {
        price_template_id: priceTemplate?._id || null,
        correlativo: priceTemplate?._id ? correlativoOf(priceTemplate as Pick<PriceTemplate, '_id'>, prefix) : null,
        lifecycle: priceTemplate?.resolvedLifecycle || priceTemplate?.lifecycle || null,
        items_count: priceTemplate?.itemsCount ?? priceTemplate?.items?.length ?? null,
        total_usd: priceTemplate?.totalPrice ?? null,
        currency: priceTemplate?.currency || null,
        customer_id: priceTemplate?.customer?.customerId || null,
        user_id: user?._id || null,
        store_id: user?.tenantId || null,
    };
}

/** Toggles "Incluir IVA" / "Incluir IGTF" de la Configuración, que gobiernan los exportables. */
export interface ExportTaxOptions {
    includeIva?: boolean;
    includeIgtf?: boolean;
}

/** Tasa del IGTF (convención NETA de docs/FISCAL-MODULE.md §2, igual que el checkout). */
export const IGTF_RATE = 0.03;
export const IGTF_LABEL = 'IGTF 3% (pago en divisas)';

/**
 * IVA de los ítems: Σ sellingPrice × amount × (1 − discountPct) × iva, en USD. `totalPrice` se
 * guarda sin IVA, así que este es el IVA que el cobro le sumaría. La misma fórmula calcula el
 * `ivaTotal` del listado en el API.
 */
export function ivaOfItems(items: Pick<PriceTemplateItem, 'sellingPrice' | 'amount' | 'discountPct' | 'iva'>[] | null | undefined): number {
    return (items || []).reduce((sum, item) => sum + (item.sellingPrice || 0) * (item.amount || 0) * (1 - (item.discountPct || 0)) * (item.iva ?? 0), 0);
}

/** IVA de una cotización: de sus ítems si vienen, o el `ivaTotal` que calcula el listado. */
export function ivaTotalOf(priceTemplate: Pick<PriceTemplate, 'items' | 'ivaTotal'> | null | undefined): number {
    if (priceTemplate?.items?.length) return ivaOfItems(priceTemplate.items);
    return priceTemplate?.ivaTotal || 0;
}

/**
 * IGTF 3% estimado si el cliente paga todo en divisas, en USD. Igual que `calculateIGTF` del
 * checkout con un único método en divisa: 3% × (neto + IVA), donde el IVA entra en la base solo si
 * el documento lo cobra ("Incluir IVA"). Una cotización en VES se asume pagada en Bs: sin IGTF.
 */
export function igtfOf(priceTemplate: Pick<PriceTemplate, 'totalPrice' | 'currency' | 'items' | 'ivaTotal'> | null | undefined, options: ExportTaxOptions): number {
    if (!options.includeIgtf || priceTemplate?.currency === 'VES') return 0;
    const base = (priceTemplate?.totalPrice || 0) + (options.includeIva ? ivaTotalOf(priceTemplate) : 0);
    if (!(base > 0)) return 0;
    return MoneyAdapter.toFloat(MoneyAdapter.calculateTax(MoneyAdapter.fromFloat(base), IGTF_RATE));
}

/** Total de los exportables en USD: `totalPrice` + IVA (si ON) + IGTF (si ON y la cotización es en USD). */
export function exportTotalOf(priceTemplate: Pick<PriceTemplate, 'totalPrice' | 'currency' | 'items' | 'ivaTotal'> | null | undefined, options: ExportTaxOptions): number {
    const iva = options.includeIva ? ivaTotalOf(priceTemplate) : 0;
    return (priceTemplate?.totalPrice || 0) + iva + igtfOf(priceTemplate, options);
}
